import React from 'react';
import { Box, Grid, Typography, CircularProgress, Paper } from '@mui/material';
import RevenueBySourceChart from './RevenueBySourceChart';
import MonthlyTrendsChart from './MonthlyTrendsChart';
import HedgePerformanceComboChart from './HedgePerformanceComboChart';
import KPICards from './KPICards';
import RegionalBreakdownChart from './RegionalBreakdownChart';
import { useQuery } from '@tanstack/react-query';
import { getMonthlyTrends, getRevenueBySource, getHedgePerformance, getRegionalPerformance } from '../services/supabase';

function Dashboard() {
  const { 
    data: revenueData, 
    error: revenueError, 
    isLoading: revenueLoading 
  } = useQuery({
    queryKey: ['revenueBySource'],
    queryFn: getRevenueBySource
  });

  const { 
    data: monthlyData, 
    error: monthlyError, 
    isLoading: monthlyLoading 
  } = useQuery({
    queryKey: ['monthlyTrends'],
    queryFn: getMonthlyTrends
  });

  const { 
    data: hedgeData, 
    error: hedgeError, 
    isLoading: hedgeLoading 
  } = useQuery({
    queryKey: ['hedgePerformance'],
    queryFn: getHedgePerformance
  });

  const { 
    data: regionalData, 
    error: regionalError, 
    isLoading: regionalLoading 
  } = useQuery({
    queryKey: ['regionalPerformance'],
    queryFn: getRegionalPerformance
  });

  const isLoading = revenueLoading || monthlyLoading || hedgeLoading || regionalLoading;
  const hasError = revenueError || monthlyError || hedgeError || regionalError;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Error loading dashboard data
        </Typography>
        <Typography variant="body1">
          {revenueError?.message || monthlyError?.message || hedgeError?.message || regionalError?.message}
        </Typography>
      </Box>
    );
  }

  // Get the most recent month from the data for KPI calculation
  const getLatestMonth = (data) => {
    if (!data || data.length === 0) return null;
    return data.reduce((latest, item) => {
      return item.month > latest ? item.month : latest;
    }, data[0].month);
  };

  const latestMonth = getLatestMonth(monthlyData);
  const latestMonthData = latestMonth ? monthlyData.find(item => item.month === latestMonth) : null;
  const previousMonth = monthlyData && monthlyData.length > 1 ? 
    monthlyData.find(item => item.month < latestMonth) : null;

  // Calculate KPI data
  const kpiData = latestMonthData ? {
    totalRevenue: latestMonthData.total_revenue,
    totalProfit: latestMonthData.profit,
    energyProduction: latestMonthData.energy_production_kwh,
    profitChange: previousMonth ? 
      ((latestMonthData.profit - previousMonth.profit) / previousMonth.profit * 100) : 0
  } : null;

  return (
    <Box>
      <Typography variant="h4" gutterBottom mb={3}>
        Financial Performance Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* KPI Cards Section */}
        <Grid item xs={12}>
          <KPICards data={kpiData} />
        </Grid>

        {/* Revenue by Source Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Revenue by Energy Source
            </Typography>
            <RevenueBySourceChart data={revenueData} />
          </Paper>
        </Grid>

        {/* Monthly Trends Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Monthly Revenue & Cost Trends
            </Typography>
            <MonthlyTrendsChart data={monthlyData} />
          </Paper>
        </Grid>

        {/* Hedge Performance Chart */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Weather Hedge Performance
            </Typography>
            <HedgePerformanceComboChart data={hedgeData} />
          </Paper>
        </Grid>

        {/* Regional Breakdown Chart */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Regional Performance Breakdown
            </Typography>
            <RegionalBreakdownChart data={regionalData} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
